import * as React from 'react'
import * as Redux from 'react-redux'
import styled, { css } from 'styled-components'

import * as consent from '@owl-nest/consent'
import { t } from '@owl-nest/localize'
import * as model from '@owl-nest/models'

import * as plume from '@ulule/owl-kit-components/next'

import * as UFE from '../../../UFE'
import { RootState } from '../../../reducers'
import * as projectModel from '../../models/project'
import { SplashTitle } from './components/SplashTitle'

type ContextProps = {
  iframe: boolean
}

export function Context({ iframe }: ContextProps): React.ReactElement<ContextProps> {
  const project = Redux.useSelector<RootState, projectModel.Project>((state) => state.application.project.data)

  const title = model.project.name<projectModel.ExtraFields>(project)
  const subtitle = model.project.subtitle<projectModel.ExtraFields>(project)
  const mainImage = model.project.mainImage<projectModel.ExtraFields>(project)
  const video = model.project.video<projectModel.ExtraFields>(project)

  return (
    <ContextWrapper $iframe={iframe} $hasVideo={video !== undefined}>
      <Information>
        <SplashTitle text={t('Coming soon!')} />

        <plume.styles.heading.L>{title || t('something very cool')}</plume.styles.heading.L>
        <plume.styles.heading.XL>{title || t('something very cool')}</plume.styles.heading.XL>

        {subtitle && <plume.styles.copy.M.light>{subtitle}</plume.styles.copy.M.light>}
      </Information>

      {video && (
        <ProjectVideoContainer>
          <ProjectVideo
            consentWall={consent.media.Wall}
            placeholder={mainImage ?? video?.thumbnail_url}
            srcSet={mainImage ?? video?.thumbnail_url}
            videoHtml={video.html}
          />
        </ProjectVideoContainer>
      )}
      {!video && mainImage && <ProjectImage src={mainImage} />}
    </ContextWrapper>
  )
}

const ProjectImage = styled.img`
  border-radius: 4px;
  display: block;
  height: 100%;
  width: 97%;
  aspect-ratio: 30 / 17;
  object-fit: cover;
  margin-left: 8px;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    width: 50%;
  }

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    max-width: 460px;
    margin-left: 16px;
    width: 100%;
    box-shadow: ${plume.COLORS.PRIMARY_WHITE} -16px 16px 0px;
  }

  @media screen and ${plume.BREAKPOINTS.LAPTOP_L} {
    max-width: 600px;
  }
`

const ProjectVideo = styled(plume.VideoLoader)`
  display: block;
  width: 100%;
`

const ProjectVideoContainer = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 4px;
  height: fit-content;
  width: 100%;

  img {
    aspect-ratio: 30 / 17;
  }

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    width: 50%;
  }

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    width: 460px;
    margin-left: 16px;
    box-shadow: ${plume.COLORS.PRIMARY_WHITE} -16px 16px 0px;
  }

  @media screen and ${plume.BREAKPOINTS.LAPTOP_L} {
    width: 600px;
  }
`

const Information = styled.div`
  margin-bottom: 20px;
  min-width: 260px;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    margin-right: 15px;
  }

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    margin-bottom: 0px;
    max-width: 400px;
    margin-right: 35px;
  }
`

const ContextWrapper = styled.div<{ $iframe: boolean; $hasVideo: boolean }>`
  padding: 16px;

  ${plume.styles.copy.M.light} {
    margin-top: 8px;
  }

  ${plume.styles.heading.XL} {
    display: none;
  }

  ${SplashTitle} {
    display: block;
    margin: 10px auto 25px auto;
    width: fit-content;
  }

  ${({ $iframe, $hasVideo }) => {
    if ($iframe && $hasVideo) {
      return css`
        min-width: 630px;
      `
    }
  }}

  ${({ $iframe }) => {
    if ($iframe) {
      return css`
        ${Information} {
          max-width: 460px;

          @media screen and ${plume.BREAKPOINTS.LAPTOP} {
            min-width: 460px;
          }
        }
      `
    }
  }};

  @media not screen and ${plume.BREAKPOINTS.LAPTOP} {
    align-items: center;
  }

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    display: flex;
    justify-content: space-between;
    padding: 20px;
  }

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    max-width: 1210px;
    margin: 0px;
    margin-top: 60px;
    margin-bottom: 60px;
    position: relative;
    background-image: url('${UFE.IMAGES.comingSoonBackground}');

    ${SplashTitle} {
      position: absolute;
      top: 10px;
    }

    ${plume.styles.heading.L} {
      display: none;
    }

    ${plume.styles.heading.XL} {
      display: block;
      margin-top: 40px;
      margin-bottom: 16px;
    }
  }

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    padding: 60px 90px 0px;
  }
`
